export interface CropParameters {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ViewportState {
  zoom: number;
  panX: number;
  panY: number;
  containerWidth: number;
  containerHeight: number;
  imageWidth: number;
  imageHeight: number;
}

export class CloudinaryUtils {
  /**
   * Checks if a URL is a Cloudinary URL
   */
  static isCloudinaryUrl(url: string): boolean {
    return url.includes('res.cloudinary.com') || url.includes('cloudinary.com');
  }

  /**
   * Parses a Cloudinary URL to extract components
   */
  static parseCloudinaryUrl(url: string): {
    baseUrl: string;
    cloudName: string;
    version: string;
    publicId: string;
    transformations?: string;
  } | null {
    try {
      // Handle URLs with and without existing transformations
      const urlWithTransformations = /https:\/\/res\.cloudinary\.com\/([^\/]+)\/image\/upload\/(.+?)\/v(\d+)\/(.+)/;
      const urlWithoutTransformations = /https:\/\/res\.cloudinary\.com\/([^\/]+)\/image\/upload\/v(\d+)\/(.+)/;
      
      let match = url.match(urlWithTransformations);
      let hasTransformations = true;
      
      if (!match) {
        match = url.match(urlWithoutTransformations);
        hasTransformations = false;
      }
      
      if (!match) return null;
      
      let cloudName, version, publicId, transformations;
      
      if (hasTransformations) {
        [, cloudName, transformations, version, publicId] = match;
      } else {
        [, cloudName, version, publicId] = match;
        transformations = undefined;
      }
      
      return {
        baseUrl: `https://res.cloudinary.com/${cloudName}/image/upload`,
        cloudName,
        version,
        publicId,
        transformations
      };
    } catch (error) {
      console.error('Error parsing Cloudinary URL:', error);
      return null;
    }
  }

  /**
   * Calculates crop parameters based on viewport state with enhanced precision
   */
  static calculateCropParameters(viewportState: ViewportState): CropParameters {
    const { zoom, panX, panY, containerWidth, containerHeight, imageWidth, imageHeight } = viewportState;
    
    // Validate inputs
    if (!imageWidth || !imageHeight || !containerWidth || !containerHeight) {
      console.warn('Invalid dimensions provided to calculateCropParameters:', viewportState);
      return { x: 0, y: 0, width: imageWidth || 1, height: imageHeight || 1 };
    }
    
    // Calculate how CSS object-cover displays the image
    const containerAspectRatio = containerWidth / containerHeight;
    const imageAspectRatio = imageWidth / imageHeight;
    
    // Determine the scale factor based on object-cover behavior
    let scale: number;
    let offsetX = 0;
    let offsetY = 0;
    
    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container - height fills, width is cropped
      scale = containerHeight / imageHeight;
      // Calculate how much width is cropped off on each side
      const scaledImageWidth = imageWidth * scale;
      offsetX = (scaledImageWidth - containerWidth) / 2;
    } else {
      // Image is taller than container - width fills, height is cropped
      scale = containerWidth / imageWidth;
      // Calculate how much height is cropped off on top/bottom
      const scaledImageHeight = imageHeight * scale;
      offsetY = (scaledImageHeight - containerHeight) / 2;
    }
    
    // Calculate the visible area in original image coordinates
    // Account for zoom and pan transformations
    const effectiveScale = scale * zoom;
    
    // Calculate crop dimensions (what's visible in the container)
    let cropWidth = containerWidth / effectiveScale;
    let cropHeight = containerHeight / effectiveScale;
    
    // Calculate crop position accounting for object-cover centering and user pan
    // Start from the center offset due to object-cover
    let cropX = offsetX / effectiveScale;
    let cropY = offsetY / effectiveScale;
    
    // Apply user pan - convert from display coordinates to image coordinates
    // Pan values are in display pixels, need to convert to original image coordinates
    // Negative pan values because pan direction is opposite to crop offset
    cropX -= panX / effectiveScale;
    cropY -= panY / effectiveScale;
    
    // Ensure crop area stays within image boundaries
    cropX = Math.max(0, Math.min(cropX, imageWidth - cropWidth));
    cropY = Math.max(0, Math.min(cropY, imageHeight - cropHeight));
    
    // Adjust dimensions if they exceed boundaries
    cropWidth = Math.min(cropWidth, imageWidth - cropX);
    cropHeight = Math.min(cropHeight, imageHeight - cropY);
    
    // Ensure minimum crop size
    cropWidth = Math.max(1, cropWidth);
    cropHeight = Math.max(1, cropHeight);
    
    const result = {
      x: Math.round(cropX),
      y: Math.round(cropY),
      width: Math.round(cropWidth),
      height: Math.round(cropHeight)
    };
    
    console.log('Enhanced crop calculation:', {
      input: { 
        imageSize: { width: imageWidth, height: imageHeight },
        containerSize: { width: containerWidth, height: containerHeight },
        zoom, panX, panY 
      },
      calculated: { 
        scale, effectiveScale, 
        objectCoverOffset: { x: offsetX, y: offsetY },
        aspectRatios: { container: containerAspectRatio, image: imageAspectRatio }
      },
      result
    });
    
    return result;
  }

  /**
   * Generates a cropped Cloudinary URL based on viewport state
   */
  static generateCroppedUrl(originalUrl: string, viewportState: ViewportState): string | null {
    if (!this.isCloudinaryUrl(originalUrl)) {
      console.warn('URL is not a Cloudinary URL:', originalUrl);
      return null;
    }

    const parsed = this.parseCloudinaryUrl(originalUrl);
    if (!parsed) {
      console.error('Failed to parse Cloudinary URL:', originalUrl);
      return null;
    }

    const cropParams = this.calculateCropParameters(viewportState);
    
    // Build crop transformation string
    const cropTransformation = `c_crop,x_${cropParams.x},y_${cropParams.y},w_${cropParams.width},h_${cropParams.height}`;
    const qualityTransformation = 'q_auto,f_auto';
    
    // Combine transformations
    const allTransformations = [cropTransformation, qualityTransformation].join('/');
    
    // Build the new URL
    const croppedUrl = `${parsed.baseUrl}/${allTransformations}/v${parsed.version}/${parsed.publicId}`;
    
    return croppedUrl;
  }

  /**
   * Gets the natural dimensions of an image from URL (requires loading)
   */
  static async getImageDimensions(url: string): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };
      
      img.onerror = () => {
        console.error('Failed to load image for dimension calculation:', url);
        resolve(null);
      };
      
      img.src = url;
    });
  }

  /**
   * Helper method to get viewport state from DOM elements
   */
  static getViewportStateFromElement(
    imageElement: HTMLImageElement,
    containerElement: HTMLElement,
    zoom: number,
    panX: number,
    panY: number
  ): ViewportState | null {
    try {
      const containerRect = containerElement.getBoundingClientRect();
      
      return {
        zoom,
        panX,
        panY,
        containerWidth: containerRect.width,
        containerHeight: containerRect.height,
        imageWidth: imageElement.naturalWidth,
        imageHeight: imageElement.naturalHeight
      };
    } catch (error) {
      console.error('Error getting viewport state:', error);
      return null;
    }
  }

  /**
   * Generate a cropped Cloudinary URL from direct crop parameters
   */
  static generateCroppedUrlFromParams(
    originalUrl: string,
    cropParams: { x: number; y: number; width: number; height: number }
  ): string | null {
    if (!this.isCloudinaryUrl(originalUrl)) {
      return null;
    }

    const parsed = this.parseCloudinaryUrl(originalUrl);
    if (!parsed) {
      console.error('Failed to parse Cloudinary URL:', originalUrl);
      return null;
    }

    try {
      // Build crop transformation string
      const cropTransformation = `c_crop,x_${cropParams.x},y_${cropParams.y},w_${cropParams.width},h_${cropParams.height}`;
      const qualityTransformation = 'q_auto,f_auto';
      
      // Combine transformations
      const allTransformations = [cropTransformation, qualityTransformation].join('/');
      
      // Build the new URL
      const croppedUrl = `${parsed.baseUrl}/${allTransformations}/v${parsed.version}/${parsed.publicId}`;
      
      return croppedUrl;
    } catch (error) {
      console.error('Error generating cropped URL from params:', error);
      return null;
    }
  }
}